/**
 * Sistema de sincronização de dados entre servidor e IndexedDB
 */

import * as indexedDB from './indexedDBService'
import { getClientes } from './clienteService'
import { getProdutos } from './produtoService'
import { getIndustrias } from './industriaService'
import { apiClient } from './apiClient'
import type { Cliente, Produto, Industria } from '../types'

export interface SyncStatus {
  clientes: { synced: boolean; count: number; lastSync?: number }
  produtos: { synced: boolean; count: number; lastSync?: number }
  industrias: { synced: boolean; count: number; lastSync?: number }
  pendingActions: number
}

/**
 * Sincroniza todos os dados do servidor para o IndexedDB
 */
export async function syncAllData(): Promise<SyncStatus> {
  console.log('🔄 Iniciando sincronização de dados...')

  const status: SyncStatus = {
    clientes: { synced: false, count: 0 },
    produtos: { synced: false, count: 0 },
    industrias: { synced: false, count: 0 },
    pendingActions: 0,
  }

  // Só sincroniza se estiver online
  if (!navigator.onLine) {
    console.log('📡 Offline - usando dados em cache')
    return status
  }

  try {
    // Sincroniza clientes
    try {
      const clientes = await getClientes()
      await indexedDB.putMany(indexedDB.STORES.CLIENTES, clientes)
      await indexedDB.markSynced('clientes')
      status.clientes = {
        synced: true,
        count: clientes.length,
        lastSync: Date.now(),
      }
      console.log(`✅ ${clientes.length} clientes sincronizados`)
    } catch (error) {
      console.error('❌ Erro ao sincronizar clientes:', error)
    }

    // Sincroniza produtos
    try {
      const produtos = await getProdutos()
      await indexedDB.putMany(indexedDB.STORES.PRODUTOS, produtos)
      await indexedDB.markSynced('produtos')
      status.produtos = {
        synced: true,
        count: produtos.length,
        lastSync: Date.now(),
      }
      console.log(`✅ ${produtos.length} produtos sincronizados`)
    } catch (error) {
      console.error('❌ Erro ao sincronizar produtos:', error)
    }

    // Sincroniza indústrias
    try {
      const industrias = await getIndustrias()
      await indexedDB.putMany(indexedDB.STORES.INDUSTRIAS, industrias)
      await indexedDB.markSynced('industrias')
      status.industrias = {
        synced: true,
        count: industrias.length,
        lastSync: Date.now(),
      }
      console.log(`✅ ${industrias.length} indústrias sincronizadas`)
    } catch (error) {
      console.error('❌ Erro ao sincronizar indústrias:', error)
    }

    // Conta ações pendentes
    status.pendingActions = await indexedDB.count(indexedDB.STORES.SYNC_QUEUE)

    console.log('✅ Sincronização concluída:', status)
    return status
  } catch (error) {
    console.error('❌ Erro na sincronização:', error)
    return status
  }
}

/**
 * Verifica se precisa sincronizar (dados com mais de 1 hora)
 */
export async function needsSync(): Promise<boolean> {
  const ONE_HOUR = 3600000
  
  const clientesStale = await indexedDB.isDataStale('clientes', ONE_HOUR)
  const produtosStale = await indexedDB.isDataStale('produtos', ONE_HOUR)
  const industriasStale = await indexedDB.isDataStale('industrias', ONE_HOUR)

  return clientesStale || produtosStale || industriasStale
}

/**
 * Obtém status atual da sincronização
 */
export async function getSyncStatus(): Promise<SyncStatus> {
  const clientesCount = await indexedDB.count(indexedDB.STORES.CLIENTES)
  const produtosCount = await indexedDB.count(indexedDB.STORES.PRODUTOS)
  const industriasCount = await indexedDB.count(indexedDB.STORES.INDUSTRIAS)
  const pendingCount = await indexedDB.count(indexedDB.STORES.SYNC_QUEUE)

  const clientesLastSync = await indexedDB.getMetadata('lastSync_clientes')
  const produtosLastSync = await indexedDB.getMetadata('lastSync_produtos')
  const industriasLastSync = await indexedDB.getMetadata('lastSync_industrias')

  return {
    clientes: {
      synced: clientesCount > 0,
      count: clientesCount,
      lastSync: clientesLastSync,
    },
    produtos: {
      synced: produtosCount > 0,
      count: produtosCount,
      lastSync: produtosLastSync,
    },
    industrias: {
      synced: industriasCount > 0,
      count: industriasCount,
      lastSync: industriasLastSync,
    },
    pendingActions: pendingCount,
  }
}

/**
 * Adiciona ação à fila de sincronização
 */
export async function addToSyncQueue(
  type: 'CREATE' | 'UPDATE' | 'DELETE',
  entity: 'pedido' | 'cliente' | 'produto' | 'industria',
  data: any
): Promise<number> {
  const action = {
    type,
    entity,
    data,
    timestamp: Date.now(),
    retries: 0,
  }

  const db = await indexedDB.initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([indexedDB.STORES.SYNC_QUEUE], 'readwrite')
    const store = transaction.objectStore(indexedDB.STORES.SYNC_QUEUE)
    const request = store.add(action)

    request.onsuccess = () => resolve(request.result as number)
    request.onerror = () => reject(request.error)
  })
}

/**
 * Processa fila de sincronização
 */
export async function processSyncQueue(): Promise<{ success: number; failed: number }> {
  if (!navigator.onLine) {
    console.log('📡 Offline - sincronização adiada')
    return { success: 0, failed: 0 }
  }

  const queue = await indexedDB.getAll<any>(indexedDB.STORES.SYNC_QUEUE)
  let success = 0
  let failed = 0

  console.log(`🔄 Processando ${queue.length} ações pendentes...`)

  for (const action of queue) {
    console.log('📤 Processando ação:', {
      id: action.id,
      type: action.type,
      entity: action.entity,
      retries: action.retries || 0,
      data: action.data
    })

    try {
      let newItem: any = null

      // Envia para o servidor de acordo com o tipo
      switch (action.entity) {
        case 'cliente':
          if (action.type === 'CREATE') {
            // Remove campos que não devem ser enviados ao criar
            const { id, createdAt, updatedAt, ...clienteData } = action.data
            console.log('📤 Enviando POST /clientes com dados:', clienteData)
            const response = await apiClient.post<Cliente>('/clientes', clienteData)
            newItem = response.data
            console.log('✅ Resposta do servidor:', newItem)
            
            // Remove o item temporário e adiciona o item real
            const tempId = action.data.id || `temp_${action.timestamp}`
            console.log(`🔄 Removendo item temporário ${tempId} e adicionando ${newItem.id}`)
            await indexedDB.remove(indexedDB.STORES.CLIENTES, tempId)
            await indexedDB.put(indexedDB.STORES.CLIENTES, newItem)
          } else if (action.type === 'UPDATE') {
            const response = await apiClient.put<Cliente>(`/clientes/${action.data.id}`, action.data)
            newItem = response.data
            await indexedDB.put(indexedDB.STORES.CLIENTES, newItem)
          } else if (action.type === 'DELETE') {
            await apiClient.delete(`/clientes/${action.data.id}`)
            await indexedDB.remove(indexedDB.STORES.CLIENTES, action.data.id)
          }
          break

        case 'produto':
          if (action.type === 'CREATE') {
            // Remove campos que não devem ser enviados ao criar
            const { id, createdAt, updatedAt, ...produtoData } = action.data
            console.log('📤 Enviando POST /produtos com dados:', produtoData)
            const response = await apiClient.post<Produto>('/produtos', produtoData)
            newItem = response.data
            console.log('✅ Produto criado:', newItem)
            
            const tempId = action.data.id || `temp_${action.timestamp}`
            await indexedDB.remove(indexedDB.STORES.PRODUTOS, tempId)
            await indexedDB.put(indexedDB.STORES.PRODUTOS, newItem)
          } else if (action.type === 'UPDATE') {
            const response = await apiClient.put<Produto>(`/produtos/${action.data.id}`, action.data)
            newItem = response.data
            await indexedDB.put(indexedDB.STORES.PRODUTOS, newItem)
          } else if (action.type === 'DELETE') {
            await apiClient.delete(`/produtos/${action.data.id}`)
            await indexedDB.remove(indexedDB.STORES.PRODUTOS, action.data.id)
          }
          break

        case 'industria':
          if (action.type === 'CREATE') {
            // Remove campos que não devem ser enviados ao criar
            const { id, createdAt, updatedAt, ...industriaData } = action.data
            console.log('📤 Enviando POST /industrias com dados:', industriaData)
            const response = await apiClient.post<Industria>('/industrias', industriaData)
            newItem = response.data
            console.log('✅ Indústria criada:', newItem)
            
            const tempId = action.data.id || `temp_${action.timestamp}`
            await indexedDB.remove(indexedDB.STORES.INDUSTRIAS, tempId)
            await indexedDB.put(indexedDB.STORES.INDUSTRIAS, newItem)
          } else if (action.type === 'UPDATE') {
            const response = await apiClient.put<Industria>(`/industrias/${action.data.id}`, action.data)
            newItem = response.data
            await indexedDB.put(indexedDB.STORES.INDUSTRIAS, newItem)
          } else if (action.type === 'DELETE') {
            await apiClient.delete(`/industrias/${action.data.id}`)
            await indexedDB.remove(indexedDB.STORES.INDUSTRIAS, action.data.id)
          }
          break

        case 'pedido':
          if (action.type === 'CREATE') {
            // Pedidos offline são tratados de forma especial
            const createResponse = await apiClient.post<{ id: string; status: string }>('/pedidos', action.data)
            const createdPedido = createResponse.data
            const tempId = action.data.id || `offline_${action.timestamp}`
            await indexedDB.remove(indexedDB.STORES.PEDIDOS_PENDENTES, tempId)

            // Se o pedido estava pendente, avança automaticamente para processamento
            const statusInicial = action.data.status || 'pendente'
            if (statusInicial === 'pendente' && createdPedido?.id) {
              await apiClient.patch(`/pedidos/${createdPedido.id}/status`, { status: 'processando' })
              console.log(`🚀 Pedido ${createdPedido.id} sincronizado e movido para processamento`)
            }
          }
          break
      }

      // Remove da fila após sucesso
      await indexedDB.remove(indexedDB.STORES.SYNC_QUEUE, action.id)
      success++
      console.log(`✅ Ação sincronizada com sucesso: ${action.entity} ${action.type}`, newItem?.id || action.data.id)
    } catch (error) {
      console.error(`❌ Erro ao sincronizar ação ${action.entity} ${action.type}:`, error)
      console.error('Dados que falharam:', action.data)
      
      // Se for erro de axios, mostra mais detalhes
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any
        console.error('Status:', axiosError.response?.status)
        console.error('Resposta:', axiosError.response?.data)
      }
      
      failed++
      
      // Incrementa tentativas
      action.retries = (action.retries || 0) + 1
      if (action.retries < 3) {
        await indexedDB.put(indexedDB.STORES.SYNC_QUEUE, action)
        console.log(`🔄 Tentativa ${action.retries}/3 para: ${action.entity} ${action.type}`)
      } else {
        // Remove após 3 tentativas
        await indexedDB.remove(indexedDB.STORES.SYNC_QUEUE, action.id)
        console.error(`❌ Falha definitiva após 3 tentativas: ${action.entity} ${action.type}`)
      }
    }
  }

  console.log(`🔄 Sincronização: ${success} sucesso, ${failed} falhas`)
  return { success, failed }
}

/**
 * Função de debug para ver a fila de sincronização
 */
export async function debugSyncQueue() {
  const queue = await indexedDB.getAll<any>(indexedDB.STORES.SYNC_QUEUE)
  console.log('🔍 FILA DE SINCRONIZAÇÃO:', queue)
  console.log(`Total de itens: ${queue.length}`)
  
  queue.forEach((item, index) => {
    console.log(`\n📋 Item ${index + 1}:`, {
      id: item.id,
      type: item.type,
      entity: item.entity,
      retries: item.retries || 0,
      timestamp: new Date(item.timestamp).toLocaleString(),
      data: item.data
    })
  })
  
  return queue
}

/**
 * Função de debug para forçar processamento da fila
 */
export async function forceSyncQueue() {
  console.log('🚀 FORÇANDO PROCESSAMENTO DA FILA...')
  const result = await processSyncQueue()
  console.log('✅ Processamento concluído:', result)
  return result
}

/**
 * Obtém a data da última sincronização (mais recente entre todas entidades)
 */
export async function getLastSyncTime(): Promise<number | null> {
  const clientesLastSync = await indexedDB.getMetadata('lastSync_clientes')
  const produtosLastSync = await indexedDB.getMetadata('lastSync_produtos')
  const industriasLastSync = await indexedDB.getMetadata('lastSync_industrias')

  const times = [
    clientesLastSync,
    produtosLastSync,
    industriasLastSync,
  ].filter(Boolean)

  if (times.length === 0) return null
  return Math.max(...times)
}
